import { Button, DotLoading, Input, type InputProps } from 'antd-mobile';
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import s from './form-controls.module.less';

export type MobileInputControlProps = Omit<InputProps, 'children'> & {
  icon?: ReactNode;
};

export function MobileInputControl(props: MobileInputControlProps) {
  const { icon, className, ...others } = props;

  return (
    <div className={s.fieldControl}>
      {icon ? <span className={s.fieldIcon}>{icon}</span> : null}
      <Input
        className={[s.input, className].filter(Boolean).join(' ')}
        {...others}
      />
    </div>
  );
}

export type MobileImageCodeRef = {
  refresh: () => Promise<void>;
};

export type MobileImageCodeProps = Omit<InputProps, 'children'> & {
  src: () => Promise<string>;
};

export const MobileImageCode = forwardRef<
  MobileImageCodeRef,
  MobileImageCodeProps
>(function MobileImageCode(props, ref) {
  const { src, value, onChange, placeholder, disabled, ...others } = props;
  const [imageSrc, setImageSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const nextImageSrc = await src();
      if (mountedRef.current) {
        setImageSrc(nextImageSrc);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [src]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  useEffect(() => {
    mountedRef.current = true;
    refresh().then(() => {});

    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  return (
    <div className={s.fieldControl}>
      <Input
        className={s.input}
        clearable
        disabled={disabled}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        {...others}
      />
      <button
        aria-label="刷新图形验证码"
        className={s.captchaImageButton}
        disabled={disabled || loading}
        onClick={() => refresh()}
        type="button"
      >
        {loading ? (
          <DotLoading />
        ) : imageSrc ? (
          <img alt="图形验证码" className={s.captchaImage} src={imageSrc} />
        ) : (
          '刷新'
        )}
      </button>
    </div>
  );
});

export type MobileMessageCodeProps = Omit<InputProps, 'children'> & {
  countdownSeconds?: number;
  onSend: () => Promise<void> | void;
  resendText?: string;
  sendText?: string;
};

export function MobileMessageCode(props: MobileMessageCodeProps) {
  const {
    countdownSeconds = 60,
    disabled,
    onSend,
    onChange,
    placeholder,
    resendText = '重新发送',
    sendText = '获取验证码',
    value,
    ...others
  } = props;
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (remaining <= 0) return;

    const timer = window.setInterval(() => {
      setRemaining((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [remaining]);

  const handleSend = useCallback(async () => {
    if (sending || remaining > 0) return;

    setSending(true);
    try {
      await onSend();
      setRemaining(countdownSeconds);
    } finally {
      setSending(false);
    }
  }, [countdownSeconds, onSend, remaining, sending]);

  const buttonText = remaining > 0 ? `${resendText} ${remaining}s` : sendText;

  return (
    <div className={s.fieldControl}>
      <Input
        className={s.input}
        clearable
        disabled={disabled}
        inputMode="numeric"
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        {...others}
      />
      <Button
        className={s.sendButton}
        color="primary"
        disabled={disabled || remaining > 0}
        fill="outline"
        loading={sending}
        onClick={handleSend}
        size="small"
      >
        {buttonText}
      </Button>
    </div>
  );
}
